import * as React from "react";
import type { DateRange } from "react-day-picker";

import { cn } from "@/src/js/lib/utils";
import { Calendar } from "../../../ui/calendar";
import { Period, formatDay, getTodayInTimeZone, toDate } from "../../periods";
import { DraftRange, isDraftRangeComplete } from "./draftRange";
import { READ_ONLY_BOX_CLASS, SECTION_TITLE_CLASS } from "./styles";

// A custom range may end in the future, so the calendar's year dropdown goes this many years past
// this one
const CALENDAR_YEARS_AHEAD = 2;

interface CustomRangeViewProps {
  draftRange: DraftRange;
  // Every reporting year, newest first
  reportingYears: Period[];
  // The group's, which decides what today is
  timeZone: string;
  onSelectRange: (range: DateRange | undefined) => void;
  // What the focus can be compared with, shown once both days are picked
  children: React.ReactNode;
}

// Choosing the focus as a range of days: its start and end, and the calendar to click them on
export function CustomRangeView({
  draftRange,
  reportingYears,
  timeZone,
  onSelectRange,
  children,
}: CustomRangeViewProps) {
  const today = getTodayInTimeZone(new Date(), timeZone);
  const oldestCalendarYear = reportingYears[reportingYears.length - 1].firstDay.year;

  return (
    <div className="tw:grid tw:min-w-0 tw:flex-1 tw:content-start tw:gap-4 tw:p-4">
      <div className="tw:flex tw:items-center tw:gap-2">
        <span className="tw:size-3 tw:rounded-sm tw:bg-(--chart-1)" aria-hidden="true" />
        <span className={SECTION_TITLE_CLASS}>Focus</span>
      </div>
      <p className="tw:m-0 tw:text-sm tw:text-neutral-500">
        Click a start date, then an end date. Click the same day twice for a single day.
      </p>
      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
        {(
          [
            ["Start date", draftRange.firstDay, "Select a start date"],
            ["End date", draftRange.lastDay, "Select an end date"],
          ] as const
        ).map(([title, day, placeholder]) => (
          <div key={title} className="tw:grid tw:gap-1">
            <span className="tw:text-xs tw:text-neutral-500">{title}</span>
            <div className={cn(READ_ONLY_BOX_CLASS, !day && "tw:text-neutral-500")}>
              {day ? formatDay(day) : placeholder}
            </div>
          </div>
        ))}
      </div>
      <Calendar
        mode="range"
        numberOfMonths={2}
        resetOnSelect
        captionLayout="dropdown"
        // No activity is older than the oldest reporting year
        startMonth={new Date(oldestCalendarYear, 0)}
        endMonth={new Date(today.year + CALENDAR_YEARS_AHEAD, 11)}
        defaultMonth={toDate(draftRange.firstDay ?? today)}
        selected={
          draftRange.firstDay
            ? {
                from: toDate(draftRange.firstDay),
                to: draftRange.lastDay ? toDate(draftRange.lastDay) : undefined,
              }
            : undefined
        }
        onSelect={onSelectRange}
      />
      {isDraftRangeComplete(draftRange) ? (
        children
      ) : (
        <p className="tw:m-0 tw:text-sm tw:text-neutral-500 tw:italic">
          The comparison choices appear once both dates are picked.
        </p>
      )}
    </div>
  );
}
